import { RotateCcw, Clock, AlertCircle, AlertTriangle } from 'lucide-react'

import { WORKFLOW_STAGE_LABELS, WORKFLOW_STAGE_COLORS } from '@/components/pm/gantt/types'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
  useStageStatistics,
  formatPercent,
  type AnalyticsFilters,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface RollbackRateChartProps {
  filters?: AnalyticsFilters
  className?: string
}

/**
 * 回退率图表组件
 * Requirements: 3.2, 3.5
 */
export function RollbackRateChart({ filters = {}, className }: RollbackRateChartProps) {
  const { data: statistics, isLoading, error } = useStageStatistics(filters)

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            阶段回退率
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Clock className="h-5 w-5 animate-spin mr-2" />
            加载中...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            阶段回退率
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-destructive">
            <AlertCircle className="h-5 w-5 mr-2" />
            加载失败
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!statistics || statistics.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            阶段回退率
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            暂无数据
          </div>
        </CardContent>
      </Card>
    )
  }

  // 按回退率排序
  const sortedStats = [...statistics].sort((a, b) => b.rollbackRate - a.rollbackRate)

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RotateCcw className="h-5 w-5" />
          阶段回退率
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {sortedStats.map((stat) => (
            <div key={stat.stage} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      'w-3 h-3 rounded-full',
                      WORKFLOW_STAGE_COLORS[stat.stage]
                    )}
                  />
                  <span className="text-sm font-medium">
                    {WORKFLOW_STAGE_LABELS[stat.stage]}
                  </span>
                  {stat.isHighRollbackRate && (
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      高回退
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">
                    {stat.rollbackCount} 次回退
                  </span>
                  <span
                    className={cn(
                      'font-medium',
                      stat.isHighRollbackRate ? 'text-red-600' : ''
                    )}
                  >
                    {formatPercent(stat.rollbackRate)}
                  </span>
                </div>
              </div>

              <Progress
                value={stat.rollbackRate * 100}
                className={cn(
                  'h-2',
                  stat.isHighRollbackRate && '[&>div]:bg-red-500'
                )}
              />
            </div>
          ))}
        </div>

        {/* 说明 */}
        <div className="mt-4 pt-4 border-t text-xs text-muted-foreground">
          <p>回退率 = 回退次数 / 总任务数 × 100%</p>
          <p>回退率超过 20% 标记为高回退率</p>
        </div>
      </CardContent>
    </Card>
  )
}
